import type {
  VoucherDocumentKind,
  VoucherDocumentState,
  VoucherLastSends,
  VoucherNotReadyReason,
  VoucherProviderDocumentState,
  VoucherSendChannel,
  VoucherSendOutcome,
  VoucherSendRecord,
  VoucherSendResult,
} from '@bandido/shared/types';

import { QueryKeys } from '@frontend/features/common/types';

/**
 * Lo que decide el botón del voucher de agencia, SIN React: qué se dibuja para
 * cada estado que publica `GET /rutero/bookings/:id/voucher`, cuándo se sigue
 * consultando después de pedir la generación, y cómo se llama el archivo. El
 * componente (`VoucherAction`) solo traduce estas decisiones a MUI, igual que
 * `ticketFetchSurfaces` gobierna a `BookingTickets`.
 */

/** Lo mínimo de una reserva para decidir si el voucher tiene superficie. */
export interface VoucherActionBooking {
  id: string;
  agencyId: number;
  state: string;
  vertical: string;
  ruteroBookingId?: string;
  source?: string;
}

/**
 * El documento existe solo para una reserva confirmada del proveedor conectado. El
 * backend contesta `not_ready · booking_not_confirmed` para lo demás; no vale
 * la pena preguntárselo para dibujar un botón apagado en una reserva `held`.
 */
export function voucherSurfaceVisible(booking: VoucherActionBooking): boolean {
  return booking.state === 'confirmed' && (Boolean(booking.ruteroBookingId) || booking.source === 'hotelrs');
}

const KEY = 'travelServices.booking.voucher';

/**
 * La clave de caché del estado, FUERA del prefijo de la lista
 * (`getTravelBookings`): la lista se invalida con cada evento de socket, cada
 * sync y cada lectura de tickets, y con N filas confirmadas eso disparaba N
 * GETs del voucher por evento. El voucher se invalida por reserva, donde el
 * evento la nombra.
 */
export function voucherQueryKey(bookingId: string): [QueryKeys, string] {
  return [QueryKeys.getTravelBookingVoucher, bookingId];
}

/** El título según lo que el documento ES: voucher, comprobante o confirmación. */
export const VOUCHER_TITLE_KEY: Record<VoucherDocumentKind, string> = {
  voucher: `${KEY}.kind.voucher`,
  confirmation: `${KEY}.kind.confirmation`,
  receipt: `${KEY}.kind.receipt`,
};

/**
 * Qué pasó con el documento del PROVEEDOR, en una frase. `none` no dice nada:
 * el proveedor no emite documento propio y no hay qué aclarar.
 */
export function voucherProviderNoteKey(
  providerDocument: VoucherProviderDocumentState,
): string | undefined {
  if (providerDocument === 'none') return undefined;
  return `${KEY}.providerDocument.${providerDocument}`;
}

export function voucherReasonKey(reason: VoucherNotReadyReason): string {
  return `${KEY}.reasons.${reason}`;
}

/**
 * Para un vertical sin celda, la alternativa que sí existe. Los circuitos
 * imprimen su itinerario desde el producto; el resto todavía no tiene nada.
 */
export function voucherUnsupportedAlternativeKey(
  vertical: string,
): string | undefined {
  return vertical === 'circuits' ? `${KEY}.alternative.circuits` : undefined;
}

export type VoucherActionView =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'generate'; titleKey: string; documentKind: VoucherDocumentKind }
  | {
      kind: 'generated';
      titleKey: string;
      documentKind: VoucherDocumentKind;
      generatedAt?: string;
      /** La reserva cambió después de emitir: hay que regenerar. */
      staleSince?: string;
      providerNoteKey?: string;
      /** Los tickets del proveedor que van aparte, por referencia. */
      attachedReferences: string[];
      /** Los últimos envíos al pasajero, por canal, si hubo. */
      lastSends?: VoucherLastSends;
    }
  | { kind: 'notReady'; reasonKey: string }
  | { kind: 'unsupported'; alternativeKey?: string };

/**
 * Una sola expresión de estado → vista. El `kind` del título sale del
 * documento GUARDADO cuando hay uno, y del que va a salir cuando todavía no.
 */
export function voucherActionView(
  state: VoucherDocumentState | undefined,
  context: { vertical: string; isError: boolean },
): VoucherActionView {
  if (state === undefined) {
    if (context.isError) return { kind: 'error' };
    return { kind: 'loading' };
  }
  const documentKind = state.kind ?? 'voucher';
  const titleKey = VOUCHER_TITLE_KEY[documentKind];
  switch (state.status) {
  case 'available':
    return { kind: 'generate', titleKey, documentKind };
  case 'generated':
    return {
      kind: 'generated',
      titleKey,
      documentKind,
      ...(state.generatedAt ? { generatedAt: state.generatedAt } : {}),
      ...(state.staleSince ? { staleSince: state.staleSince } : {}),
      ...(voucherProviderNoteKey(state.providerDocument)
        ? { providerNoteKey: voucherProviderNoteKey(state.providerDocument) }
        : {}),
      attachedReferences: state.attachedTicketReferences ?? [],
      ...(state.lastSends ? { lastSends: state.lastSends } : {}),
    };
  case 'not_ready':
    return {
      kind: 'notReady',
      reasonKey: voucherReasonKey(state.reason ?? 'service_detail_missing'),
    };
  case 'unsupported':
  default: {
    const alternativeKey = voucherUnsupportedAlternativeKey(context.vertical);
    return { kind: 'unsupported', ...(alternativeKey ? { alternativeKey } : {}) };
  }
  }
}

/** Qué chip lleva la fila del listado, o ninguno. */
export type VoucherRowChip = 'ready' | 'stale' | 'pending';

export function voucherRowChip(
  state: VoucherDocumentState | undefined,
): VoucherRowChip | undefined {
  if (!state) return undefined;
  if (state.status === 'generated') return state.staleSince ? 'stale' : 'ready';
  if (state.status === 'available') return 'pending';
  return undefined;
}

/*
 * Después del POST el documento se dibuja en un worker: el estado sigue
 * diciendo `available` (o el `generated` viejo) hasta que la cola termina. Se
 * vuelve a preguntar cada pocos segundos, y se PARA por dos motivos: porque
 * apareció un documento nuevo, o porque pasó demasiado tiempo. Sin el tope, un
 * worker caído dejaba la pantalla preguntando para siempre.
 */
export const VOUCHER_POLL_INTERVAL_MS = 2_000;
export const VOUCHER_POLL_TIMEOUT_MS = 90_000;

export interface VoucherPollingInput {
  /** Cuándo se pidió la generación; `null` si no hay pedido en vuelo. */
  requestedAt: number | null;
  now: number;
  state?: VoucherDocumentState;
  /** El `generatedAt` que había ANTES de pedir: el nuevo tiene que ser otro. */
  baselineGeneratedAt: string | null;
}

export type VoucherPollingDecision =
  | { kind: 'idle' }
  | { kind: 'poll'; intervalMs: number }
  /** Apareció un documento NUEVO: lo único que merece el aviso de "listo". */
  | { kind: 'done' }
  /** La generación ya no puede ocurrir (`not_ready`/`unsupported`): se para, sin festejar. */
  | { kind: 'unavailable' }
  | { kind: 'timedOut' };

export function voucherPollingDecision(
  input: VoucherPollingInput,
): VoucherPollingDecision {
  if (input.requestedAt === null) return { kind: 'idle' };
  const generatedAt = input.state?.status === 'generated' ? input.state.generatedAt : undefined;
  if (generatedAt && generatedAt !== input.baselineGeneratedAt) return { kind: 'done' };
  // Si la generación ya no puede ocurrir (la reserva se canceló o su desenlace
  // quedó sin resolver en el medio), seguir preguntando no cambia nada. NO es
  // lo mismo que "listo": el POST devuelve ese estado sin encolar, y con un
  // solo `done` la pantalla mostraba el motivo y un toast verde a la vez.
  if (input.state && input.state.status !== 'available' && input.state.status !== 'generated') {
    return { kind: 'unavailable' };
  }
  if (input.now - input.requestedAt >= VOUCHER_POLL_TIMEOUT_MS) return { kind: 'timedOut' };
  return { kind: 'poll', intervalMs: VOUCHER_POLL_INTERVAL_MS };
}

/**
 * El nombre del archivo lo pone el backend (`voucher-<referencia>.pdf`) en el
 * `Content-Disposition`. Se respeta cuando llega; hoy, con el API en otro
 * origen y sin `exposedHeaders` en su CORS, el navegador lo oculta y se usa el
 * de reserva (queda anotado en #2420, que toca el backend).
 */
export function voucherFilenameFrom(
  contentDisposition: string | undefined,
  fallback: string,
): string {
  const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const name = match?.[1]?.trim();
  return name && name.length > 0 ? decodeURIComponent(name) : fallback;
}

/*
 * ENVIAR. El backend devuelve cómo terminó —`resolved | partial | no_email |
 * failed | uncertain` (y `pending` mientras vuela)— y lo registra; acá solo se
 * decide qué frase le toca a cada resultado y cómo se lee el último envío.
 * Ninguno es un error HTTP: `no_email` manda a cargar el mail del contacto,
 * `failed` dice que el proveedor de mail lo RECHAZÓ, `uncertain` que se le
 * entregó y no contestó: pudo haber salido, y el asesor decide sabiéndolo.
 */
export function voucherSendToastKey(outcome: VoucherSendOutcome): {
  key: string;
  color: 'success' | 'warning' | 'error';
} {
  if (outcome === 'resolved') return { key: `${KEY}.send.sent.resolved`, color: 'success' };
  // Llegó, pero sin el ticket del operador: se avisa, no se festeja.
  if (outcome === 'partial') return { key: `${KEY}.send.sent.partial`, color: 'warning' };
  if (outcome === 'no_email') return { key: `${KEY}.send.sent.no_email`, color: 'warning' };
  if (outcome === 'failed') return { key: `${KEY}.send.sent.failed`, color: 'error' };
  // `uncertain` y `pending`: ni fracaso ni éxito, y sobre todo no "reenviá".
  return { key: `${KEY}.send.sent.${outcome}`, color: 'warning' };
}

/**
 * La IDENTIDAD del intento que viaja al backend. Cada clic es un intento
 * nuevo (un reenvío deliberado sigue siendo posible), salvo que haya un
 * intento por ese canal SIN RESOLVER: el que quedó sin respuesta (timeout del
 * cliente, red caída), o el que el estado publica como `pending` —cubre la
 * recarga de la página y una segunda pestaña—. Entonces el clic reintenta
 * ESE intento con su mismo id, y el backend devuelve lo que pasó en vez de
 * mandar un segundo mail. El `pending` no dura para siempre: el backend lo
 * lee como `uncertain` pasada su gracia, y ahí el clic vuelve a ser nuevo.
 */
export interface VoucherAttemptInFlight {
  /** De qué reserva: un intento no se hereda si el modal cambió de reserva en vuelo. */
  bookingId: string;
  channel: VoucherSendChannel;
  attemptId: string;
}
/** Los intentos atados, POR CANAL: copiar un link no toca al mail que sigue en vuelo. */
export type VoucherAttemptsInFlight = Partial<Record<VoucherSendChannel, VoucherAttemptInFlight>>;

export function voucherAttemptFor(
  bookingId: string,
  channel: VoucherSendChannel,
  bound: VoucherAttemptsInFlight,
  lastSends: VoucherLastSends | undefined,
  newId: () => string,
): string {
  const atado = bound[channel];
  if (atado && atado.bookingId === bookingId) return atado.attemptId;
  const publicado = lastSends?.[channel];
  if (publicado && publicado.outcome === 'pending') return publicado.attemptId;
  return newId();
}

/**
 * Qué queda ATADO después de una respuesta: `pending` por mail sigue atado
 * (el mismo intento sigue en el SMTP: el próximo clic lo consulta, no abre
 * otro); un resultado terminal suelta SU canal. El otro canal no se toca:
 * un link copiado no libera al mail sin resolver.
 */
export function voucherAttemptAfter(
  result: VoucherSendResult,
  attempt: VoucherAttemptInFlight,
  previous: VoucherAttemptsInFlight,
): VoucherAttemptsInFlight {
  const next: VoucherAttemptsInFlight = { ...previous };
  if (result.channel === 'email' && result.outcome === 'pending') next[attempt.channel] = attempt;
  else delete next[attempt.channel];
  return next;
}

/** Suelta el intento de UN canal (el otro no se toca). */
export function voucherAttemptUnbound(
  previous: VoucherAttemptsInFlight,
  channel: VoucherSendChannel,
): VoucherAttemptsInFlight {
  const next = { ...previous };
  delete next[channel];
  return next;
}

/**
 * El estado publica el desenlace de un intento atado: se suelta solo. Devuelve
 * el MISMO objeto si nada cambió, para no re-renderizar en vano.
 */
export function voucherAttemptsReleased(
  bound: VoucherAttemptsInFlight,
  lastSends: VoucherLastSends | undefined,
): VoucherAttemptsInFlight {
  let next: VoucherAttemptsInFlight | null = null;
  for (const channel of Object.keys(bound) as VoucherSendChannel[]) {
    const atado = bound[channel];
    const publicado = lastSends?.[channel];
    if (atado && publicado?.attemptId === atado.attemptId && publicado.outcome !== 'pending') {
      next = next ?? { ...bound };
      delete next[channel];
    }
  }
  return next ?? bound;
}

/**
 * Un pedido de envío que falla SIN veredicto del backend no es "no se pudo
 * enviar": el mail puede estar saliendo. Sin respuesta (timeout del cliente,
 * red), o con cualquier 5xx: un intermediario que se cansó antes que el API
 * (502/503/504), o el API caído a mitad de camino (500 con la base abajo
 * después de tocar el SMTP). Solo un 4xx es un veredicto (409 documento
 * viejo, 400, 403…). Se distingue para no decir lo que no se sabe y para
 * atar el intento y reintentarlo con su id.
 */
export function voucherSendRequestOutcome(error: unknown): 'unanswered' | 'rejected' {
  const response = (error as { response?: { status?: unknown } } | null)?.response;
  if (response === undefined || response === null) return 'unanswered';
  return typeof response.status === 'number' && response.status >= 500 ? 'unanswered' : 'rejected';
}

/**
 * El 409 "este intento es de otro documento": el link se pidió para un PDF
 * que después se regeneró y el frontend reintentó el id atado. No hay nada
 * que confirmar con nadie (el link no tiene efecto externo): se suelta ese
 * intento y se pide uno NUEVO para el documento de hoy, una sola vez.
 */
export const VOUCHER_ATTEMPT_STALE = 'VOUCHER_ATTEMPT_STALE';
export function voucherSendAttemptStale(error: unknown): boolean {
  const data = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data;
  return data?.code === VOUCHER_ATTEMPT_STALE;
}

/**
 * Mientras el último envío por mail está `pending`, el estado se vuelve a
 * pedir solo: el toast dice "en curso" y el asesor no tiene que adivinar
 * cuándo mirar. Acotado por el backend, que pasado su tiempo de gracia deja
 * de publicar `pending`.
 */
export const VOUCHER_SEND_POLL_INTERVAL_MS = 10_000;
export function voucherSendPolling(
  state: VoucherDocumentState | undefined,
): { kind: 'idle' } | { kind: 'poll'; intervalMs: number } {
  return state?.lastSends?.email?.outcome === 'pending'
    ? { kind: 'poll', intervalMs: VOUCHER_SEND_POLL_INTERVAL_MS }
    : { kind: 'idle' };
}

/**
 * Enviar solo lo que está VIGENTE: con `staleSince` el backend contesta 409
 * (regenerá primero) y la pantalla ya lo está pidiendo. No se ofrece un botón
 * que va a fallar.
 */
export function voucherSendAvailable(view: VoucherActionView): boolean {
  return view.kind === 'generated' && !view.staleSince;
}

/** Una línea por canal, mail primero: el link nunca esconde al mail. */
export function voucherSendNotes(
  lastSends: VoucherLastSends | undefined,
): { key: string; params: { recipient: string; date: string } }[] {
  return [voucherSendNote(lastSends?.email), voucherSendNote(lastSends?.link)].filter(
    (note): note is NonNullable<typeof note> => note !== undefined,
  );
}

/** La línea "enviado a … el …" (o el último fallo) bajo los botones. */
export function voucherSendNote(
  lastSend: VoucherSendRecord | undefined,
): { key: string; params: { recipient: string; date: string } } | undefined {
  if (!lastSend) return undefined;
  const date = new Date(lastSend.at).toLocaleString();
  const recipient = lastSend.recipient ?? '';
  if (lastSend.channel === 'link') {
    // Un link cuya fila no se cerró (`pending`, o `uncertain` pasada la
    // gracia) no se puede dar por emitido.
    const key =
      lastSend.outcome === 'pending' || lastSend.outcome === 'uncertain'
        ? `${KEY}.send.last.linkUnconfirmed`
        : lastSend.outcome === 'partial'
          ? `${KEY}.send.last.linkPartial`
          : `${KEY}.send.last.link`;
    return { key, params: { recipient, date } };
  }
  return { key: `${KEY}.send.last.${lastSend.outcome}`, params: { recipient, date } };
}

/**
 * El link que se copia: el backend devuelve una RUTA relativa al API (la
 * firma es de bandido, no de S3) y el frontend la compone con su base.
 */
export function voucherShareUrl(apiBase: string, path: string): string {
  return `${apiBase.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

